import { useMemo, useCallback } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Paper,
  Tooltip,
  IconButton,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DateRangeIcon from '@mui/icons-material/DateRange';

/**
 * Barra de filtros locales para el Dashboard de Detalle de Interacciones.
 * Todos los filtros operan sobre los datos ya cargados, sin peticiones HTTP.
 *
 * Props:
 *  - rawRows:  array original completo de Table1
 *  - filters:  { fechas, horaDesde, horaHasta, campanias, tipoContacto, sentido, causaTerminacion }
 *  - onChange:  (newFilters) => void
 */

const HORAS = Array.from({ length: 25 }, (_, i) => ({
  value: i,
  label: `${String(i).padStart(2, '0')}:00`,
}));

const FilterBarDetalleLlamadas = ({ rawRows, filters, onChange }) => {
  const theme = useTheme();
  const dk = theme.palette.mode === 'dark';

  /* ---------- Valores únicos extraídos de los datos ---------- */
  const opciones = useMemo(() => {
    if (!rawRows || rawRows.length === 0)
      return { fechas: [], campanias: [], tipos: [], sentidos: [], causas: [] };

    const fechasSet = new Set();
    const campSet = new Set();
    const tipoSet = new Set();
    const sentidoSet = new Set();
    const causaSet = new Set();

    rawRows.forEach((r) => {
      if (r.Inicio) {
        const d = new Date(r.Inicio);
        if (!isNaN(d)) fechasSet.add(d.toISOString().split('T')[0]);
      }
      if (r.NombreCampania) campSet.add(r.NombreCampania);
      if (r.TipoContacto) tipoSet.add(r.TipoContacto);
      if (r.Sentido) sentidoSet.add(r.Sentido);
      if (r.CausaTerminacion) causaSet.add(r.CausaTerminacion);
    });

    return {
      fechas: [...fechasSet].sort(),
      campanias: [...campSet].sort(),
      tipos: [...tipoSet].sort(),
      sentidos: [...sentidoSet].sort(),
      causas: [...causaSet].sort(),
    };
  }, [rawRows]);

  /* ---------- Helpers ---------- */
  const fmtFechaCorta = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    const dias = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
    const dt = new Date(iso + 'T12:00:00');
    const dia = dias[dt.getDay()];
    return `${dia} ${d}/${m}/${y}`;
  };

  /* Rango de fechas seleccionado (desde / hasta) → genera array de fechas intermedias */
  const fechaDesdeVal = filters.fechaDesde || opciones.fechas[0] || '';
  const fechaHastaVal = filters.fechaHasta || opciones.fechas[opciones.fechas.length - 1] || '';

  const fechasEnRango = useMemo(() => {
    if (!fechaDesdeVal || !fechaHastaVal || opciones.fechas.length === 0) return opciones.fechas;
    return opciones.fechas.filter((f) => f >= fechaDesdeVal && f <= fechaHastaVal);
  }, [fechaDesdeVal, fechaHastaVal, opciones.fechas]);

  /* Sync: cuando cambia el rango, actualizar filters.fechas */
  const handleFechaDesde = useCallback((e) => {
    const val = e.target.value;
    const hasta = filters.fechaHasta || opciones.fechas[opciones.fechas.length - 1] || val;
    const nuevasFechas = opciones.fechas.filter((f) => f >= val && f <= hasta);
    onChange({ ...filters, fechaDesde: val, fechas: nuevasFechas });
  }, [filters, opciones.fechas, onChange]);

  const handleFechaHasta = useCallback((e) => {
    const val = e.target.value;
    const desde = filters.fechaDesde || opciones.fechas[0] || val;
    const nuevasFechas = opciones.fechas.filter((f) => f >= desde && f <= val);
    onChange({ ...filters, fechaHasta: val, fechas: nuevasFechas });
  }, [filters, opciones.fechas, onChange]);

  const resetFilters = () => {
    onChange({
      fechas: [...opciones.fechas],
      fechaDesde: opciones.fechas[0] || '',
      fechaHasta: opciones.fechas[opciones.fechas.length - 1] || '',
      horaDesde: 0,
      horaHasta: 24,
      campanias: [],
      tipoContacto: [],
      sentido: [],
      causaTerminacion: [],
    });
  };

  const diasEnRango = fechasEnRango.length;

  /* Conteo filtrado */
  const filteredCount = useMemo(() => {
    if (!rawRows) return 0;
    return aplicarFiltrosDetalle(rawRows, filters).length;
  }, [rawRows, filters]);

  const autoSx = {
    '& .MuiInputBase-root': { minHeight: 32, fontSize: 12, py: 0 },
    '& .MuiChip-root': { height: 20, fontSize: 10 },
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 2,
        borderColor: dk ? '#334155' : '#cbd5e1',
        bgcolor: dk ? 'rgba(30,41,59,0.6)' : 'rgba(248,250,252,0.8)',
        borderRadius: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <FilterListIcon sx={{ fontSize: 18, color: dk ? '#94a3b8' : '#64748b' }} />
        <Typography variant="subtitle2" sx={{ color: dk ? '#e2e8f0' : '#334155', fontWeight: 600 }}>
          Filtros
        </Typography>
        <Typography variant="caption" sx={{ color: dk ? '#64748b' : '#94a3b8' }}>
          ({filteredCount} de {rawRows?.length || 0} interacciones)
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Restablecer filtros">
          <IconButton size="small" onClick={resetFilters}>
            <RestartAltIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Fila 1: Rango de Fechas + Horas */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        <DateRangeIcon sx={{ fontSize: 18, color: dk ? '#94a3b8' : '#64748b' }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={{ fontSize: 12 }}>Fecha desde</InputLabel>
          <Select
            value={fechaDesdeVal}
            label="Fecha desde"
            onChange={handleFechaDesde}
            sx={{ fontSize: 12, height: 32 }}
          >
            {opciones.fechas
              .filter((f) => f <= fechaHastaVal)
              .map((f) => (
                <MenuItem key={f} value={f} sx={{ fontSize: 12 }}>
                  {fmtFechaCorta(f)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={{ fontSize: 12 }}>Fecha hasta</InputLabel>
          <Select
            value={fechaHastaVal}
            label="Fecha hasta"
            onChange={handleFechaHasta}
            sx={{ fontSize: 12, height: 32 }}
          >
            {opciones.fechas
              .filter((f) => f >= fechaDesdeVal)
              .map((f) => (
                <MenuItem key={f} value={f} sx={{ fontSize: 12 }}>
                  {fmtFechaCorta(f)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <Chip
          label={`${diasEnRango} día${diasEnRango !== 1 ? 's' : ''}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontSize: 11, height: 22 }}
        />

        <Box sx={{ width: 16 }} />

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel sx={{ fontSize: 12 }}>Hora desde</InputLabel>
          <Select
            value={filters.horaDesde ?? 0}
            label="Hora desde"
            onChange={(e) => onChange({ ...filters, horaDesde: e.target.value })}
            sx={{ fontSize: 12, height: 32 }}
          >
            {HORAS.filter((h) => h.value < (filters.horaHasta ?? 24)).map((h) => (
              <MenuItem key={h.value} value={h.value} sx={{ fontSize: 12 }}>{h.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel sx={{ fontSize: 12 }}>Hora hasta</InputLabel>
          <Select
            value={filters.horaHasta ?? 24}
            label="Hora hasta"
            onChange={(e) => onChange({ ...filters, horaHasta: e.target.value })}
            sx={{ fontSize: 12, height: 32 }}
          >
            {HORAS.filter((h) => h.value > (filters.horaDesde ?? 0)).map((h) => (
              <MenuItem key={h.value} value={h.value} sx={{ fontSize: 12 }}>{h.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Fila 2: Tipo, Sentido, Campaña, Causa */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Autocomplete
          multiple
          size="small"
          options={opciones.tipos}
          value={filters.tipoContacto || []}
          onChange={(_, v) => onChange({ ...filters, tipoContacto: v })}
          renderInput={(params) => <TextField {...params} label="Tipo Contacto" InputLabelProps={{ sx: { fontSize: 12 } }} />}
          sx={{ minWidth: 180, flex: 1, ...autoSx }}
          disableCloseOnSelect
        />

        <Autocomplete
          multiple
          size="small"
          options={opciones.sentidos}
          value={filters.sentido || []}
          onChange={(_, v) => onChange({ ...filters, sentido: v })}
          renderInput={(params) => <TextField {...params} label="Sentido" InputLabelProps={{ sx: { fontSize: 12 } }} />}
          sx={{ minWidth: 180, flex: 1, ...autoSx }}
          disableCloseOnSelect
        />

        <Autocomplete
          multiple
          size="small"
          options={opciones.campanias}
          value={filters.campanias || []}
          onChange={(_, v) => onChange({ ...filters, campanias: v })}
          renderInput={(params) => <TextField {...params} label="Campaña" InputLabelProps={{ sx: { fontSize: 12 } }} />}
          sx={{ minWidth: 220, flex: 1, ...autoSx }}
          disableCloseOnSelect
        />

        <Autocomplete
          multiple
          size="small"
          options={opciones.causas}
          value={filters.causaTerminacion || []}
          onChange={(_, v) => onChange({ ...filters, causaTerminacion: v })}
          renderInput={(params) => <TextField {...params} label="Causa Terminación" InputLabelProps={{ sx: { fontSize: 12 } }} />}
          sx={{ minWidth: 220, flex: 1, ...autoSx }}
          disableCloseOnSelect
        />
      </Box>
    </Paper>
  );
};

/**
 * Aplica los filtros locales sobre las filas crudas de Detalle Interacciones.
 * Exportada para ser usada en DashboardDetalleLlamadas.
 */
export function aplicarFiltrosDetalle(rows, filters) {
  if (!rows || rows.length === 0) return [];
  if (!filters) return rows;

  let filtered = rows;

  // Filtro por fechas seleccionadas
  if (filters.fechas && filters.fechas.length > 0) {
    filtered = filtered.filter((r) => {
      if (!r.Inicio) return false;
      const d = new Date(r.Inicio);
      if (isNaN(d)) return false;
      return filters.fechas.includes(d.toISOString().split('T')[0]);
    });
  }

  // Filtro por rango horario
  const hDesde = filters.horaDesde ?? 0;
  const hHasta = filters.horaHasta ?? 24;
  if (hDesde > 0 || hHasta < 24) {
    filtered = filtered.filter((r) => {
      if (!r.Inicio) return true;
      const d = new Date(r.Inicio);
      if (isNaN(d)) return true;
      const h = d.getHours() + d.getMinutes() / 60;
      return h >= hDesde && h < hHasta;
    });
  }

  // Filtro por campañas
  if (filters.campanias && filters.campanias.length > 0) {
    filtered = filtered.filter((r) => filters.campanias.includes(r.NombreCampania));
  }

  // Filtro por tipo de contacto
  if (filters.tipoContacto && filters.tipoContacto.length > 0) {
    filtered = filtered.filter((r) => filters.tipoContacto.includes(r.TipoContacto));
  }

  // Filtro por sentido
  if (filters.sentido && filters.sentido.length > 0) {
    filtered = filtered.filter((r) => filters.sentido.includes(r.Sentido));
  }

  // Filtro por causa de terminación
  if (filters.causaTerminacion && filters.causaTerminacion.length > 0) {
    filtered = filtered.filter((r) => filters.causaTerminacion.includes(r.CausaTerminacion));
  }

  return filtered;
}

export default FilterBarDetalleLlamadas;
